<template>
  <ModalComponent
    ref="modalRef"
    size="xl"
    submitButtonText=""
    noFooter
    :beforeClose="mayClose"
    @close="finish"
  >
    <template #title>{{ heading }}</template>
    <template #body>
      <LoadingIndicatorComponent
        v-if="editor.loading"
        :message="$t('forwards.pools.editor.loading')"
      />

      <div v-else-if="editor.notFound" class="text-center py-5">
        <FontAwesomeIcon
          icon="fa-solid fa-lock"
          size="2x"
          class="text-body-tertiary mb-3"
        />
        <h5>{{ $t("forwards.pools.editor.not_found_title") }}</h5>
        <p class="text-body-tertiary mb-0">
          {{ $t("forwards.pools.editor.not_found_body") }}
        </p>
      </div>

      <template v-else>
        <div
          v-if="infeasiblePeriods.length > 0"
          class="advisory-note mb-3 infeasibility-warning"
          role="status"
        >
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{
            $t("forwards.pools.infeasible_periods", {
              periods: infeasiblePeriods.join(", "),
            })
          }}</span>
        </div>

        <PoolEscrowRemainingComponent
          v-if="editor.escrowRemaining !== null"
          :rows="editor.escrowRemaining"
        />

        <template v-else>
          <WizardStepRail
            v-if="inWizard"
            :steps="wizardSteps"
            :currentIndex="step"
            :stepLabel="$t('common.step')"
            class="mb-4"
          />

          <div
            v-else
            class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3"
          >
            <p class="text-body-tertiary mb-0">
              {{ $t("forwards.pools.editor.description") }}
            </p>
            <div
              v-if="editor.canEdit && !editor.isNew"
              class="d-flex align-items-center gap-2 flex-wrap"
            >
              <!-- A pool that cannot be deleted can be archived, so its blocked reason is not shown. -->
              <ActionButtonWithSpinner
                v-if="editor.capabilities?.can_delete"
                :busy="editor.busyAction === 'delete'"
                variant="btn-outline-danger"
                @click="onDelete"
              >
                {{ $t("forwards.pools.editor.delete") }}
              </ActionButtonWithSpinner>
              <ActionButtonWithSpinner
                v-if="editor.capabilities?.can_archive"
                :busy="editor.busyAction === 'archive'"
                variant="btn-ghost"
                @click="onArchive"
              >
                {{ $t("forwards.pools.editor.archive") }}
              </ActionButtonWithSpinner>
              <ActionButtonWithSpinner
                v-if="editor.capabilities?.can_unarchive"
                :busy="editor.busyAction === 'unarchive'"
                variant="btn-ghost"
                @click="onUnarchive"
              >
                {{ $t("forwards.pools.editor.unarchive") }}
              </ActionButtonWithSpinner>
            </div>
          </div>

          <div v-if="!editor.canEdit" class="advisory-note mb-3">
            <FontAwesomeIcon icon="fa-regular fa-circle-info" />
            <span>{{ $t("forwards.pools.editor.read_only") }}</span>
          </div>

          <div
            v-if="editor.saveError !== null"
            class="error-note mb-3"
            role="alert"
          >
            <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
            <span>{{
              editor.saveErrorMessage ?? $t("forwards.pools.editor.save_failed")
            }}</span>
          </div>

          <template v-if="shows('warnings')">
            <p
              v-for="(warning, index) in allWarnings"
              :key="index"
              class="advisory-note mb-2"
            >
              <FontAwesomeIcon icon="fa-regular fa-circle-info" />
              <span>{{ warning }}</span>
            </p>
          </template>

          <!-- One template for both views, as the PPA editor: the wizard shows a step's sections, the
               full form every one (`visibleSections`); the form state lives in the editor, so a step
               left and returned to keeps what was entered. -->
          <div class="d-flex flex-column gap-4 pb-4">
            <!-- A new pool's first step: someone creating one may be new to forward sales. -->
            <ForwardSalesGuideComponent
              v-if="inWizard && step === 0"
              class="wizard-guide"
            />

            <WizardSection
              v-if="shows('details')"
              :title="$t('forwards.pools.editor.details_title')"
            >
              <div class="mb-3">
                <label class="form-label" :for="nameId"
                  >{{ $t("forwards.pools.editor.name") }}
                  <InfoTooltip
                    v-if="editor.canEdit"
                    :text="$t('forwards.pools.editor.name_hint')"
                /></label>
                <input
                  :id="nameId"
                  :value="editor.form.name"
                  type="text"
                  class="form-control"
                  :maxlength="POOL_NAME_MAX"
                  :class="{ 'is-invalid': editor.form.name.trim() === '' }"
                  :disabled="!editor.canEdit"
                  @input="onNameInput"
                />
              </div>
              <div>
                <label class="form-label" :for="descriptionId"
                  >{{ $t("forwards.pools.editor.pool_description") }}
                  <InfoTooltip
                    v-if="editor.canEdit"
                    :text="$t('forwards.pools.editor.pool_description_hint')"
                /></label>
                <textarea
                  :id="descriptionId"
                  v-model="descriptionText"
                  class="form-control"
                  rows="3"
                  :maxlength="POOL_DESCRIPTION_MAX"
                  :disabled="!editor.canEdit"
                ></textarea>
              </div>
            </WizardSection>

            <WizardSection
              v-if="shows('options')"
              :title="$t('forwards.pools.options.title')"
            >
              <PoolOptionsCardComponent
                v-model:minimumPrice="editor.form.minimum_price"
                v-model:contangoCondition="editor.form.contango_condition"
                v-model:archiveOnDelivery="editor.form.archive_on_delivery"
                :minimumPriceError="editor.minimumPriceProblem"
                :disabled="!editor.canEdit"
              />
              <PoolAdvancedListingComponent
                v-if="isStaff"
                v-model="editor.form.advanced_listing"
                :simpleShape="simpleShape"
                :disabled="!editor.canEdit"
                class="mt-3"
              />
            </WizardSection>

            <WizardSection
              v-if="shows('members')"
              :title="$t('forwards.pools.members.title')"
              :description="$t('forwards.pools.members.description')"
            >
              <PoolMembersPickerComponent
                v-model="editor.form.member_company_ids"
                :candidates="editor.memberCandidates"
                :blockers="editor.blockers"
                :disabled="!editor.canEdit"
              />
            </WizardSection>

            <WizardSection
              v-if="shows('devices')"
              :title="$t('forwards.pools.devices.title')"
              :description="$t('forwards.pools.devices.description')"
            >
              <PoolDevicePanelComponent
                v-model:deviceScope="editor.form.device_scope"
                v-model:allowlistDeviceIds="editor.form.allowlist_device_ids"
                :devices="editor.devices"
                :loading="editor.devicesLoading"
                :disabled="!editor.canEdit"
              />
            </WizardSection>

            <WizardSection
              v-if="shows('listing')"
              :title="$t('forwards.pools.volumes.title')"
            >
              <!-- The full form offers the box among the options; the wizard has none, so its listing step does. -->
              <PoolAdvancedListingComponent
                v-if="isStaff && inWizard"
                v-model="editor.form.advanced_listing"
                :simpleShape="simpleShape"
                :disabled="!editor.canEdit"
                class="mb-3"
              />
              <PoolVolumesEditorComponent
                :listingKind="editor.form.listing_kind"
                :entryWidth="editor.entryWidth"
                @select:kind="editor.setListingKind"
                @select:width="editor.setEntryWidth"
                v-model:recurringVolumes="editor.recurringDraft.volumes"
                v-model:listings="editor.oneOffListings"
                :preview="editor.preview"
                :previewLoading="editor.previewLoading"
                :listingChanged="editor.listingChanged"
                :disabled="!editor.canEdit"
                :simple="simpleListing"
              />
              <!-- In the simple editor too: a production range is not a listing shape (`hasSimpleShape`). -->
              <PoolProductionRangeComponent
                v-if="editor.form.listing_kind !== 'one_off'"
                v-model:limited="editor.form.production_limited"
                v-model:from="editor.form.production_from"
                v-model:until="editor.form.production_until"
                :error="editor.productionRangeProblem"
                :disabled="!editor.canEdit"
                class="mt-3"
              />
              <!-- The wizard creates a pool on the default strategy; it is chosen afterwards in the full form. -->
              <PoolSalesStrategyComponent
                v-if="!inWizard && !payAsProduced"
                v-model="editor.form.sales_plan"
                :committedAboveRelease="editor.preview?.committed_above_release"
                :releaseChanges="editor.preview?.release_changes"
                :sellAhead="!simpleListing"
                :disabled="!editor.canEdit"
                class="mt-3"
              />
              <!-- The wizard shows the schedule in its summary instead, beside the listing it releases. -->
              <PoolSalesScheduleButtonComponent
                v-if="!inWizard && !payAsProduced"
                :schedule="salesSchedule"
                class="mt-3"
              />
            </WizardSection>

            <WizardSection
              v-if="shows('name')"
              :title="$t('forwards.pools.editor.name')"
            >
              <PoolNameFieldComponent
                :name="editor.form.name"
                :edited="editor.nameEdited"
                @edit="editor.setName"
              />
            </WizardSection>

            <WizardSection
              v-if="shows('summary')"
              :title="$t('forwards.pools.summary.title')"
            >
              <PoolSummaryComponent
                :form="editor.form"
                :salesSchedule
                :memberNames="editor.memberNames"
                :devices="editor.poolDevices"
              />
              <p
                v-for="blocker in editor.blockers"
                :key="blocker"
                class="error-note mt-3 mb-0"
              >
                <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
                <span>{{ blocker }}</span>
              </p>
            </WizardSection>

            <WizardSection
              v-if="shows('eligibility') && eligibility"
              :title="$t('forwards.pools.eligibility.title')"
              :description="$t('forwards.pools.eligibility.description')"
            >
              <PoolEligibilityIndicatorComponent
                :indicator="eligibility"
                :devices="editor.poolDevices"
              />
            </WizardSection>

            <WizardSection
              v-if="shows('commitments')"
              :title="$t('forwards.pools.commitments.title')"
              :description="$t('forwards.pools.commitments.description')"
            >
              <PoolCommitmentsTableComponent
                :rows="editor.commitments"
                :loading="editor.loading"
              />
            </WizardSection>
          </div>
        </template>
      </template>

      <WizardFooter v-if="inWizard && showsForm">
        <template #start>
          <button
            v-if="step > 0"
            type="button"
            class="btn btn-ghost"
            @click="step--"
          >
            <FontAwesomeIcon icon="arrow-left" class="me-2" />
            {{ $t("common.back") }}
          </button>
          <!-- Every step, unlike the PPA wizard's first only: the full form is the way to the
               settings the wizard skips, wherever the user is when they want them. -->
          <button type="button" class="btn btn-link" @click="view = 'all'">
            <FontAwesomeIcon icon="list" class="me-2" />
            {{ $t("production_strategy.show_all_as_one_form") }}
          </button>
        </template>
        <template #end>
          <span class="text-body-tertiary fs-9">
            {{
              $t("production_strategy.step_x_of_y", {
                current: step + 1,
                total: wizardSteps.length,
              })
            }}
          </span>
          <button
            type="button"
            class="btn btn-ghost"
            @click="modalRef?.handleModalClose()"
          >
            {{ $t("common.cancel") }}
          </button>
          <button
            v-if="!onLastStep"
            type="button"
            class="btn btn-primary"
            :disabled="!currentStep.ready"
            @click="step++"
          >
            {{ $t("common.continue") }}
            <FontAwesomeIcon icon="arrow-right" class="ms-2" />
          </button>
          <ActionButtonWithSpinner
            v-else
            :busy="editor.saving"
            :disabled="!currentStep.ready"
            variant="btn-primary"
            @click="onSave()"
          >
            {{ $t("forwards.pools.wizard.create") }}
          </ActionButtonWithSpinner>
        </template>
      </WizardFooter>

      <WizardFooter v-else>
        <template #start>
          <span v-if="showsForm && editor.isDirty" role="status">
            <FontAwesomeIcon icon="fa-regular fa-circle-info" class="me-1" />
            {{ $t("forwards.pools.editor.unsaved_changes") }}
          </span>
        </template>
        <template #end>
          <template v-if="showsForm && editor.isDirty">
            <button
              type="button"
              class="btn btn-sm btn-ghost"
              @click="editor.discard()"
            >
              {{ $t("forwards.pools.editor.discard") }}
            </button>
            <!-- Force lifts a feasibility block; it cannot lift a body the route will not parse. -->
            <ActionButtonWithSpinner
              v-if="editor.canForceSave"
              :busy="editor.saving"
              :disabled="editor.formInvalid"
              variant="btn-outline-danger"
              @click="onSave(true)"
            >
              {{ $t("forwards.pools.editor.save_anyway") }}
            </ActionButtonWithSpinner>
            <ActionButtonWithSpinner
              :busy="editor.saving"
              :disabled="!editor.canSave"
              variant="btn-primary"
              @click="onSave()"
            >
              {{ $t("forwards.pools.editor.save") }}
            </ActionButtonWithSpinner>
          </template>
          <button
            v-else
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="modalRef?.handleModalClose()"
          >
            {{ $t("common.close") }}
          </button>
        </template>
      </WizardFooter>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import WizardFooter from "@/components/common/WizardFooter.vue";
import WizardSection from "@/components/common/WizardSection.vue";
import WizardStepRail from "@/components/common/WizardStepRail.vue";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { useUnsavedChangesGuard } from "@/composables/useUnsavedChangesGuard";
import ForwardSalesGuideComponent from "@/forwards2/components/ForwardSalesGuideComponent.vue";
import { readsForwardsAsStaff } from "@/forwards2/forwardsGuard";
import PoolAdvancedListingComponent from "@/forwards2/pools/PoolAdvancedListingComponent.vue";
import PoolCommitmentsTableComponent from "@/forwards2/pools/PoolCommitmentsTableComponent.vue";
import PoolDevicePanelComponent from "@/forwards2/pools/PoolDevicePanelComponent.vue";
import PoolEligibilityIndicatorComponent from "@/forwards2/pools/PoolEligibilityIndicatorComponent.vue";
import PoolEscrowRemainingComponent from "@/forwards2/pools/PoolEscrowRemainingComponent.vue";
import PoolMembersPickerComponent from "@/forwards2/pools/PoolMembersPickerComponent.vue";
import PoolNameFieldComponent from "@/forwards2/pools/PoolNameFieldComponent.vue";
import PoolOptionsCardComponent from "@/forwards2/pools/PoolOptionsCardComponent.vue";
import PoolProductionRangeComponent from "@/forwards2/pools/PoolProductionRangeComponent.vue";
import PoolSalesScheduleButtonComponent from "@/forwards2/pools/PoolSalesScheduleButtonComponent.vue";
import PoolSalesStrategyComponent from "@/forwards2/pools/PoolSalesStrategyComponent.vue";
import PoolSummaryComponent from "@/forwards2/pools/PoolSummaryComponent.vue";
import PoolVolumesEditorComponent from "@/forwards2/pools/PoolVolumesEditorComponent.vue";
import {
  hasSimpleShape,
  usesSimpleListing,
} from "@/forwards2/pools/poolListingView";
import {
  POOL_DESCRIPTION_MAX,
  POOL_NAME_MAX,
} from "@/forwards2/pools/poolFormRules";
import { usePoolEditor } from "@/forwards2/pools/usePoolEditor";
import { uniq } from "lodash-es";
import { computed, onMounted, ref, useId, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { onBeforeRouteUpdate } from "vue-router";

/**
 * Create and edit a pool, in a modal: a null `poolId` opens a blank form, an id an existing pool.
 * Pages render it through `PoolEditorModalHost`, and it is mounted for one editing session, which
 * ends on `closed`: the host unmounts it. `closed` reports whether the session wrote anything, so
 * the page can reload what it shows.
 *
 * A new pool is a wizard, as the PPA editor is: companies and devices, then the listing, then a
 * read-only summary with the deal eligibility and a name generated from the choices until its
 * creator edits it. Name, description and options wait for the full form, which an existing pool
 * opens on and "show all" switches the wizard to, keeping what was entered. A create closes the
 * modal; step 3 has already shown the preview's warnings. Delete closes it too; archive and
 * unarchive reload it.
 *
 * Every save sends the whole form because `PUT /pools/<id>` replaces the pool, so a partial body
 * would delete the listings or members it left out. The device allowlist is editable from the
 * start: an unsaved pool asks the backend for the candidate devices of the companies the form
 * names, so an allowlist pool takes one save rather than two.
 *
 * A viewer whose only claim on the pool is escrow it left behind gets the read-only escrow card
 * instead of the form — the response carries nothing else to edit.
 */
const props = defineProps<{
  poolId: number | null;
}>();

const emit = defineEmits<{
  /** @param written - whether a save, archive, unarchive or delete went through */
  closed: [written: boolean];
}>();

type Section =
  | "warnings"
  | "details"
  | "options"
  | "members"
  | "devices"
  | "listing"
  | "name"
  | "summary"
  | "eligibility"
  | "commitments";

/** A wizard step: its sections, and whether it lets the user go on — the last step's being the create. */
interface WizardStepView {
  title: string;
  sections: readonly Section[];
  ready: boolean;
}

/** The full form's sections (the template sets their order); the commitments only once the pool exists. */
const FULL_FORM: readonly Section[] = [
  "warnings",
  "details",
  "options",
  "members",
  "devices",
  "listing",
  "eligibility",
  "commitments",
];

const { t } = useI18n();
const editor = usePoolEditor(props.poolId);
const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");
const accountStore = useAccountStore();
const written = ref(false);
const view = ref<"wizard" | "all">(props.poolId === null ? "wizard" : "all");
const step = ref(0);

const nameId = useId();
const descriptionId = useId();

const showsForm = computed(
  () => !editor.loading && !editor.notFound && editor.escrowRemaining === null
);
const infeasiblePeriods = computed(
  () => editor.detail?.infeasible_periods ?? []
);
const eligibility = computed(
  () => editor.preview?.eligibility ?? editor.detail?.eligibility ?? null
);

const inWizard = computed(() => view.value === "wizard");

const wizardSteps = computed<WizardStepView[]>(() => [
  {
    title: t("forwards.pools.wizard.step_members"),
    sections: ["members", "devices"],
    ready: editor.membersReady,
  },
  {
    title: t("forwards.pools.wizard.step_listing"),
    sections: ["listing"],
    ready: editor.listingReady,
  },
  {
    title: t("forwards.pools.wizard.step_confirm"),
    sections: ["warnings", "name", "summary", "eligibility"],
    ready: editor.canSave,
  },
]);

const currentStep = computed(() => wizardSteps.value[step.value]);
const onLastStep = computed(() => step.value === wizardSteps.value.length - 1);

const visibleSections = computed<ReadonlySet<Section>>(() => {
  if (inWizard.value) return new Set(currentStep.value.sections);
  return new Set(
    editor.isNew
      ? FULL_FORM.filter((section) => section !== "commitments")
      : FULL_FORM
  );
});

const shows = (section: Section) => visibleSections.value.has(section);

/** Staff choose per pool between the simple and the advanced listing editor; producers get the choice. */
const isStaff = computed(() => readsForwardsAsStaff(accountStore.account));
const simpleShape = computed(() => hasSimpleShape(editor.form));

const payAsProduced = computed(
  () => editor.form.listing_kind === "pay_as_produced"
);
const salesSchedule = computed(() => editor.preview?.sales_schedule ?? null);

/**
 * Whether the listing shows only a recurring listing's quarters (`usesSimpleListing`). Judged on
 * the form, so the staff's box takes effect as it is ticked.
 */
const simpleListing = computed(() => usesSimpleListing(editor.form));

const heading = computed(() =>
  editor.isNew
    ? t("forwards.pools.editor.new_title")
    : (editor.detail?.pool.name ?? t("forwards.pools.editor.title"))
);

// The last save and the live preview evaluate the same pool, so they repeat each other's
// sentences; each one is worth showing once (RV-pools-10).
const allWarnings = computed(() =>
  uniq([...editor.warnings, ...editor.previewWarnings])
);

const descriptionText = computed<string>({
  get: () => editor.form.description ?? "",
  set: (value) => {
    editor.form.description = value === "" ? null : value;
  },
});

// Covers leaving the page while the modal is open; `mayClose` covers the modal's own close controls.
useUnsavedChangesGuard(() => editor.isDirty);

// The load assigns a fresh form, which the editor's form watchers answer with a debounced preview and
// candidate-device refresh — refreshing here as well sent the preview twice on every open.
onMounted(() => {
  if (modalRef.value) modalRef.value.showModal = true;
  void editor.loadCandidateCompanies();
  void editor.load();
});

// A link in the editor that stays on the same route component (the deal page, for another deal)
// unmounts nothing, so the editor closes itself rather than staying open over the new page.
onBeforeRouteUpdate(() => {
  if (!mayClose()) return false;
  finish();
});

const mayClose = () =>
  !editor.isDirty || window.confirm(t("forwards.pools.editor.close_confirm"));

/** The session's one way out, whichever control or event ended it. */
const finish = () => emit("closed", written.value);

const onNameInput = (event: Event) =>
  editor.setName((event.target as HTMLInputElement).value);

const onSave = async (force = false) => {
  const creating = editor.isNew;
  if ((await editor.save(force ? { force: true } : {})) === null) return;
  written.value = true;
  if (creating) finish();
};

const onDelete = async () => {
  if (!window.confirm(t("forwards.pools.editor.confirm_delete"))) return;
  if (!(await editor.remove())) return;
  written.value = true;
  finish();
};

const onArchive = async () => {
  if (!(await editor.archive())) return;
  written.value = true;
  await editor.reload();
};

const onUnarchive = async () => {
  if (!(await editor.unarchive())) return;
  written.value = true;
  await editor.reload();
};
</script>

<style scoped lang="scss">
// The sections' own horizontal padding (`WizardSection`), so the guide's toggle lines up with their titles.
.wizard-guide {
  padding: 0 28px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
}
</style>
